<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import beedroomNoiseReductionCurtainsAndBlinds from './images/bedroom-noise-reduction-curtains-and-blinds.jpg';
	import manInstallingSoundproofingWindowInserts from './images/man-installing-soundproofing-window-inserts.jpg';
	import plantRugsOtherSoftDecorHelpAbsorbSound from './images/plant-rugs-other-soft-decor-help-absorb-sound.jpg';
	import soundBarriesAndShutters from './images/sound-barries-and-shutters.jpg';
	import womanInstallingWindowInserts from './images/woman-installing-window-inserts.jpg';
</script>

<Section class="max-md:pt-0">
	<Image
		alt="A person wearing a purple shirt is installing a window insert."
		src={manInstallingSoundproofingWindowInserts}
		class="w-full"
		loading="eager"
	>
		{#snippet descriptionSlot()}
			<p>
				<a
					href="https://www.instagram.com/p/CbgJ3eABaza/"
					target="_blank"
					rel="external">Source</a
				>
			</p>
		{/snippet}
	</Image>
	<p>
		If you’re a DIYer, you’re a problem solver. And one of the biggest problems
		in a home might be the most surprising: excessive noise. If you live on a
		busy street or in a high-traffic downtown area, you’re already familiar with
		the many sounds of a vibrant neighborhood.
	</p>
	<p>
		But if you’re a renter or have a smaller budget, you might not want to
		splurge on something like window replacements or a sound engineer to tackle
		soundproofing your windows.
	</p>
	<p>
		There are a few ways to improve the acoustic performance of the windows in
		your home. Here are some of the best strategies we’ve found for DIYers like
		you.
	</p>
</Section>

<Section>
	<h2>What You’ll Need for Soundproofing Windows</h2>
	<p>
		Before you dive into your noise-canceling project, there are a few steps to
		cover before you get to work (or purchase anything).
	</p>
	<ol
		class="
    list-decimal
    marker:font-bold marker:text-indow-grey-dark
  "
	>
		<li>
			<p class="font-bold">Measure and account for all of your windows.</p>
			<p>
				Assess all of your windows and calculate the size of each frame. If
				you’re adding curtains to the design, you’ll want to measure the frame
				and account for the length and width of the curtain panels you want —
				and any hardware that you’ll have to install.
			</p>
		</li>
		<li>
			<p class="font-bold">Consider the investment you’ll make.</p>
			<p>
				There are a few headache-free, inexpensive ways to prevent noise from
				infiltrating your space. Ask yourself a few key questions about the
				project:
			</p>
			<ul>
				<li>Are you working with a smaller budget?</li>
				<li>Do you have the time to wait for new windows to be installed?</li>
				<li>Are you renting your home?</li>
			</ul>
			<p>
				Maybe you need faster, more affordable, and more effective solutions
				than replacement windows. If the products you use are <a
					href="/performance"
				>
					tested for effective soundproofing
				</a>, you might not need to splurge on a full replacement.
			</p>
		</li>

		<li>
			<p class="font-bold">Think about your soundproofing options:</p>
			<p>
				There are products that will prevent noise in your space, but if you
				spend some time researching options, you’ll note that some offer other
				benefits to your home design. Think about your dream home and what you
				value most — are you after products that will last for decades? Do you
				want lots of natural light without the cooling bills? Want a more
				comfortable and
				<a href="/resources/soundproofing-windows"> energy-efficient home </a>?
				Some products might offer you more than a calming space, so think about
				all the benefits you want out of your DIY project.
			</p>
		</li>
		<li>
			<p class="font-bold">Style and soundproofing can go hand in hand.</p>
			<p>
				The products you use to soundproof should ideally
				<a href="/resources/soundproofing-windows"
					>complement the look and feel</a
				>
				of your home’s design, whether you live in a modern or more traditional home.
				It’s important to note how old your home is, too. There are building codes
				that require specific window designs on historic homes, and replacing them
				might require permitting and a higher budget.
			</p>
		</li>
	</ol>
</Section>

<Section>
	<h2>Soundproofing Solutions + Strategies</h2>
	<p>
		Let’s take a look at some soundproofing materials and products that will
		offer you options for noise cancellation — and solutions for a more
		comfortable home.
	</p>
	<p>
		Not all materials and products will have the <a
			href="/resources/blog/most-effective-soundproofing-materials"
			>same impact on your space</a
		>, so consider the kind of noise you’re trying to cancel out — whether it’s
		outside traffic, noise from a lobby or stairwell or your upstairs neighbors.
	</p>
	<p>
		Here are a few options to consider, regardless of how you might need to
		reduce noise.
	</p>
</Section>

<Section>
	<h2>Noise-Canceling Curtains</h2>
	<Image
		alt="This bedroom has two sets of curtains, one that is a light material and then a heavy-duty curtain that is on top."
		src={beedroomNoiseReductionCurtainsAndBlinds}
		class="w-full"
	>
		{#snippet descriptionSlot()}
			<p>
				<a
					href="https://www.pexels.com/photo/classic-interior-of-bedroom-with-panoramic-window-7061073"
					target="_blank"
					rel="external">Source</a
				>
			</p>
		{/snippet}
	</Image>
	<p>
		Noise-reducing curtains and blinds offer a stylish solution for
		soundproofing your home and are often heavier than traditional curtains.
		You’ll have to ensure that the material is wide and long enough to cover the
		entire window for optimal soundproofing. These products are an inexpensive
		option for homeowners who want less noise in the home and a touch of style.
		The only catch? The curtains need to be closed in order to block noise and
		the cracks around the curtains will let some noise through.
	</p>
</Section>

<Section>
	<h2>Stylish Decor (and Plants!)</h2>
	<Image
		alt="Interior of a home featuring a sitting area with a large rug, monstera plants, and other house plants. A circular mirror and wooden cabinet are against a light green wall in the background."
		src={plantRugsOtherSoftDecorHelpAbsorbSound}
		class="w-full"
	>
		{#snippet descriptionSlot()}
			<p>
				<a
					href="https://unsplash.com/photos/YI2YkyaREHk"
					target="_blank"
					rel="external"
				>
					Source
				</a>
			</p>
		{/snippet}
	</Image>
	<p>
		Plants, rugs, and other soft decor can help to absorb sound and prevent
		reverberation. These kinds of materials can add more sound protection and as
		a bonus, they’ll amp up the personality of your home without extending your
		budget too much.
	</p>
</Section>

<Section>
	<h2>Sound Barriers and Shutters</h2>
	<Image
		alt="Two green shutters are on each side of a window."
		src={soundBarriesAndShutters}
		class="w-full"
	>
		{#snippet descriptionSlot()}
			<p>
				<a
					href="https://www.pexels.com/photo/glass-window-with-wooden-shutters-6307762"
					target="_blank"
					rel="external">Source</a
				>
			</p>
		{/snippet}
	</Image>
	<p>
		Adding products that act as sound barriers will also help to prevent outside
		noise, but require additional labor and a bit of remodeling. Adding shutters
		to your exterior window design also adds a layer of noise protection but
		comes with the challenge of installing them.
	</p>
	<p>
		Installing an additional layer of drywall will keep out the noise throughout
		your entire space and amps up your home’s insulation, but it’s a solution
		that might require some demolition.
	</p>
</Section>

<Section>
	<h2>Window Inserts</h2>
	<Image
		src={womanInstallingWindowInserts}
		alt="A person installs a soundproofing window insert from Indow into a window frame."
		class="w-full"
	>
		{#snippet descriptionSlot()}
			<p>
				<a href="/solutions/window-noise-reduction">Source</a>
			</p>
		{/snippet}
	</Image>
	<p>
		Window inserts offer homeowners and renters an efficient and inexpensive
		solution for soundproofing, even if you live in a historic home. <a
			href="/performance">Indow window inserts</a
		> provide up to 70% reduction in noise coming through your windows, and their
		insulative qualities can reduce energy consumption in the home by up to 20%.
	</p>
</Section>

<Section>
	<h2>A Quieter, Comfier Home</h2>
	<p>
		Whether you’re in an apartment building or historic home, there are a few
		ways to ensure your <a href="/resources/blog/noise-pollution-human-health"
			>living space is comfortable</a
		> and quiet for years to come.
	</p>
	<p>
		Using solutions like window inserts from Indow also offer energy efficiency
		and UV protection to your interior design without blowing your budget. Check
		out even more <a href="/resources/soundproofing-windows"
			>tips for soundproofing</a
		> and protecting your home from outside noise
	</p>
	<p>
		Want to know more about optimal soundproofing for your home? You can <a
			href="/how-to/get-indow-inserts"
		>
			get a free estimate
		</a>
		for your project here. If you are ready to get started, you can
		<a href="/about-indow/how-to-order"> order your inserts </a> today!
	</p>
</Section>

<Reviews />
